'use client';

import { useEffect } from 'react';
import { signOut, useSession } from 'next-auth/react';

// Module-level guards so the fetch patch is installed once and signOut fires a
// single time, even with React StrictMode's double-invoked effects.
let interceptorInstalled = false;
let signingOut = false;

const forceSignOut = () => {
  if (signingOut) return;
  signingOut = true;
  signOut({ callbackUrl: '/login?expired=1' });
};

const getRequestUrl = (input: RequestInfo | URL): string => {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
};

/**
 * Watches for an expired Strapi token and redirects the user to the login page.
 *
 * The Strapi JWT is embedded in the next-auth session but has its own fixed
 * lifetime, so it can expire while the next-auth session is still considered
 * valid. This guard covers both detection paths:
 *  - the next-auth callbacks flag `session.error === 'TokenExpired'`, and
 *  - any proxied `/api/*` request returning 401 (e.g. a revoked token).
 */
export default function SessionGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: session } = useSession();

  // A — react to the expiry detected server-side in the next-auth callbacks.
  useEffect(() => {
    if (session?.error === 'TokenExpired') {
      forceSignOut();
    }
  }, [session?.error]);

  // B — intercept any 401 coming from the Strapi proxy and sign the user out.
  useEffect(() => {
    if (interceptorInstalled) return;
    interceptorInstalled = true;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      if (response.status === 401) {
        const url = getRequestUrl(args[0]);
        // Only react to our own Strapi proxy. The next-auth endpoints under
        // /api/auth legitimately return 401 during the sign-in handshake.
        if (url.includes('/api/') && !url.includes('/api/auth/')) {
          forceSignOut();
        }
      }

      return response;
    };
  }, []);

  return <>{children}</>;
}
